/**
 * The dope sheet itself: one `<canvas>` plus pointer handling.
 *
 * Every mutation goes through store actions (never state writes), continuous key drags are
 * wrapped in `beginKeyDrag()` / `endKeyDrag()` so a whole drag is ONE undo entry, and all
 * geometry lives in `layout.ts` so the interaction rules stay testable.
 */

import { identityQuat } from '../../shared';
import type { CanonicalBoneId, ClipEvent, KeyframeSelection } from '../../shared';
import type { EditorContext } from '../context';
import type { ClipEventRef } from '../types';
import { selectEvaluatedPose } from '../index';
import { openKeyContextMenu, type OpenMenu } from './contextMenu';
import { selectionKeySet, sheetRows, summaryFrames } from './frames';
import {
  boneIndexAtY,
  boxSelectKeys,
  createMetrics,
  eventIndexAtX,
  FALLBACK_WIDTH,
  frameToX,
  hitTestKeys,
  isOnEventBand,
  keyDragStep,
  normalizeRect,
  pixelsPerFrame,
  rectSize,
  rowAtY,
  rowsTop,
  sheetHeight,
  xToFrame,
  type Rect,
  type SheetRow,
  type TimelineMetrics,
} from './layout';
import { drawSheet } from './render';

export interface DopesheetHandle {
  element: HTMLElement;
  canvas: HTMLCanvasElement;
  /** Repaint immediately from current store state. */
  redraw(): void;
  dispose(): void;
}

/** Movement (in CSS px) below which a press-drag-release still counts as a click. */
const CLICK_SLOP = 3;
/** Keep this many pixels of lead-in visible when auto-scrolling during playback. */
const AUTOSCROLL_MARGIN = 24;

/** Shared empty array, so a clip with no events does not allocate one per redraw. */
const EMPTY_EVENTS: readonly ClipEvent[] = [];

type Drag =
  | { kind: 'scrub' }
  | { kind: 'keys'; startFrame: number; anchorFrame: number; candidates: number[]; applied: number }
  | { kind: 'band'; x0: number; y0: number; additive: boolean; moved: boolean }
  /** Dragging one event marker along the band. `ref` follows the marker as it moves. */
  | { kind: 'event'; ref: ClipEventRef };

export function mountDopesheet(container: HTMLElement, ctx: EditorContext): DopesheetHandle {
  const store = ctx.store;

  const element = document.createElement('div');
  element.className = 'tl-sheet';
  const canvas = document.createElement('canvas');
  canvas.className = 'tl-canvas';
  canvas.tabIndex = 0;
  element.appendChild(canvas);
  container.appendChild(element);

  let drag: Drag | null = null;
  let band: Rect | null = null;
  let menu: OpenMenu | null = null;
  let disposed = false;

  // -------------------------------------------------------------------------
  // Measuring
  // -------------------------------------------------------------------------

  function rowsNow(): SheetRow[] {
    return sheetRows(store.getState());
  }

  function metricsFor(rowCount: number): TimelineMetrics {
    const timeline = store.getState().timeline;
    const rect = canvas.getBoundingClientRect();
    const width = rect.width || element.clientWidth || FALLBACK_WIDTH;
    const base = createMetrics({ width, zoom: timeline.zoom, scrollFrame: timeline.scrollFrame });
    return { ...base, height: sheetHeight(rowCount, base) };
  }

  function pointerPos(event: { clientX: number; clientY: number }): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  // -------------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------------

  function redraw(): void {
    if (disposed) return;
    const state = store.getState();
    const rows = rowsNow();
    const m = metricsFor(rows.length);

    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
    const pixelWidth = Math.max(1, Math.round(m.width * dpr));
    const pixelHeight = Math.max(1, Math.round(m.height * dpr));
    if (canvas.width !== pixelWidth) canvas.width = pixelWidth;
    if (canvas.height !== pixelHeight) canvas.height = pixelHeight;
    canvas.style.height = `${m.height}px`;

    if (state.mode !== 'animate') return;
    autoScroll(m, state.playhead.frame, state.playhead.playing);

    const c = canvas.getContext('2d');
    if (!c) return;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawSheet(c, {
      metrics: m,
      rows,
      summaryFrames: summaryFrames(
        state.clip,
        rows.map((row) => row.bone),
      ),
      selected: selectionKeySet(state),
      selectedBone: state.selection.bone,
      playhead: state.playhead.frame,
      durationFrames: state.clip?.durationFrames ?? 0,
      band,
      events: state.clip?.events ?? EMPTY_EVENTS,
      selectedEvent: state.selectedEvent,
    });
  }

  /** Keep the playhead on screen while playing (never fights the user while paused). */
  function autoScroll(m: TimelineMetrics, playhead: number, playing: boolean): void {
    if (!playing || drag) return;
    const x = frameToX(playhead, m);
    if (x >= m.laneWidth && x <= m.width - AUTOSCROLL_MARGIN) return;
    const span = Math.max(1, xToFrame(m.width, m) - xToFrame(m.laneWidth, m));
    store.actions.setScrollFrame(Math.max(0, playhead - span * 0.1));
  }

  // -------------------------------------------------------------------------
  // Interaction helpers
  // -------------------------------------------------------------------------

  function scrubTo(x: number, m: TimelineMetrics): void {
    store.actions.setPlayhead(Math.round(xToFrame(x, m)));
  }

  /** Frames drawn on the "All" row for the given lanes. */
  function summaryFor(rows: readonly SheetRow[]): number[] {
    return summaryFrames(
      store.getState().clip,
      rows.map((row) => row.bone),
    );
  }

  function hitAt(x: number, y: number, rows: readonly SheetRow[], m: TimelineMetrics) {
    return hitTestKeys(x, y, rows, summaryFor(rows), m);
  }

  /** What a hit resolves to: one key, or every key on that frame for a summary-row hit. */
  function refsForHit(
    rows: readonly SheetRow[],
    hit: { bone: CanonicalBoneId | null; frame: number },
  ): KeyframeSelection {
    if (hit.bone) return [{ bone: hit.bone, frame: hit.frame }];
    return rows
      .filter((row) => row.frames.includes(hit.frame))
      .map((row) => ({ bone: row.bone, frame: hit.frame }));
  }

  function isWholeSelectionAlready(refs: KeyframeSelection): boolean {
    const selection = store.getState().keySelection;
    return refs.every((ref) =>
      selection.some((s) => s.bone === ref.bone && s.frame === ref.frame),
    );
  }

  function beginKeyDrag(
    rows: readonly SheetRow[],
    anchorFrame: number,
    startFrame: number,
  ): void {
    const selected = selectionKeySet(store.getState());
    const candidates = new Set<number>([store.getState().playhead.frame]);
    for (const row of rows) {
      for (const frame of row.frames) {
        if (!selected.has(`${row.bone}:${frame}`)) candidates.add(frame);
      }
    }
    store.actions.beginKeyDrag();
    drag = {
      kind: 'keys',
      startFrame,
      anchorFrame,
      candidates: [...candidates],
      applied: 0,
    };
  }

  function updateKeyDrag(state: Drag & { kind: 'keys' }, x: number, m: TimelineMetrics): void {
    const step = keyDragStep({
      anchorFrame: state.anchorFrame,
      startFrame: state.startFrame,
      pointerFrame: xToFrame(x, m),
      candidates: state.candidates,
      snap: store.getState().timeline.snap,
      zoom: m.zoom,
      applied: state.applied,
    });
    if (step === 0) return;
    state.applied += step;
    store.actions.moveSelectedKeys(step);
  }

  // -------------------------------------------------------------------------
  // Pointer events
  // -------------------------------------------------------------------------

  function onPointerDown(event: PointerEvent): void {
    if (store.getState().mode !== 'animate' || event.button !== 0) return;
    menu?.close();
    menu = null;
    canvas.focus();

    const rows = rowsNow();
    const m = metricsFor(rows.length);
    const { x, y } = pointerPos(event);
    const additive = event.shiftKey || event.ctrlKey || event.metaKey;

    // Bone-name column: select the bone whose row was clicked.
    if (x < m.laneWidth) {
      const row = rowAtY(y, m);
      const bone = row >= 1 ? rows[row - 1]?.bone : undefined;
      if (bone) store.actions.selectBone(bone);
      return;
    }

    // Ruler: scrub.
    if (y < m.rulerHeight) {
      drag = { kind: 'scrub' };
      capture(event);
      scrubTo(x, m);
      return;
    }

    // Event band: pick up a marker, or select nothing. Checked BEFORE the key hit test, because
    // the band sits above the rows and `hitAt` would otherwise be asked about a y that belongs to
    // no row at all.
    if (isOnEventBand(y, m)) {
      const events = store.getState().clip?.events ?? EMPTY_EVENTS;
      const index = eventIndexAtX(x, events, m);
      if (index >= 0) {
        const hitEvent = events[index];
        const ref = { frame: hitEvent.frame, name: hitEvent.name };
        store.actions.selectEvent(ref);
        drag = { kind: 'event', ref };
        // Opens the coalesce group, so the whole drag collapses into one undo entry.
        store.actions.beginEventDrag();
        capture(event);
      } else {
        store.actions.selectEvent(null);
      }
      redraw();
      return;
    }

    const hit = hitAt(x, y, rows, m);
    if (hit) {
      const refs = refsForHit(rows, hit);
      // Pressing an already-selected key starts a drag of the whole selection.
      if (!isWholeSelectionAlready(refs) || additive) {
        store.actions.selectKeys(refs, { additive });
      }
      capture(event);
      beginKeyDrag(rows, hit.frame, xToFrame(x, m));
      return;
    }

    drag = { kind: 'band', x0: x, y0: y, additive, moved: false };
    capture(event);
  }

  function onPointerMove(event: PointerEvent): void {
    if (!drag) return;
    const rows = rowsNow();
    const m = metricsFor(rows.length);
    const { x, y } = pointerPos(event);

    if (drag.kind === 'scrub') {
      scrubTo(x, m);
      return;
    }
    if (drag.kind === 'keys') {
      updateKeyDrag(drag, x, m);
      return;
    }
    if (drag.kind === 'event') {
      const frame = Math.max(0, Math.round(xToFrame(x, m)));
      if (frame !== drag.ref.frame) {
        store.actions.moveEvent(drag.ref, frame);
        // The marker may have been clamped to the clip's legal range, so the ref has to come from
        // the state the action actually produced — not from the pointer.
        const moved = store.getState().selectedEvent;
        if (moved) drag.ref = moved;
      }
      redraw();
      return;
    }
    if (Math.abs(x - drag.x0) > CLICK_SLOP || Math.abs(y - drag.y0) > CLICK_SLOP) {
      drag.moved = true;
    }
    band = normalizeRect(drag.x0, drag.y0, x, y);
    redraw();
  }

  function onPointerUp(event: PointerEvent): void {
    if (!drag) return;
    const current = drag;
    drag = null;

    if (current.kind === 'keys') {
      store.actions.endKeyDrag();
      return;
    }
    if (current.kind === 'event') {
      store.actions.endEventDrag();
      return;
    }
    if (current.kind === 'scrub') return;

    const rows = rowsNow();
    const m = metricsFor(rows.length);
    const { x, y } = pointerPos(event);
    const rect = normalizeRect(current.x0, current.y0, x, y);
    band = null;

    if (current.moved && rectSize(rect) > CLICK_SLOP) {
      store.actions.selectKeys(boxSelectKeys(rect, rows, m), { additive: current.additive });
    } else {
      // A plain click on empty sheet: move the playhead and drop the key selection.
      if (!current.additive) store.actions.clearKeySelection();
      scrubTo(x, m);
    }
    redraw();
  }

  function onDoubleClick(event: MouseEvent): void {
    const state = store.getState();
    if (state.mode !== 'animate' || !state.clip) return;
    const rows = rowsNow();
    const m = metricsFor(rows.length);
    const { x, y } = pointerPos(event);
    if (x < m.laneWidth) return;

    // Double-clicking the event band adds a marker there. This branch also has to come before the
    // guard below: `rowsTop`, not `rulerHeight`, is where the rows begin, and letting a band
    // double-click through would insert a KEYFRAME on whatever row index the arithmetic produced.
    if (isOnEventBand(y, m)) {
      store.actions.addEvent(Math.max(0, Math.round(xToFrame(x, m))));
      redraw();
      return;
    }
    if (y < rowsTop(m)) return;

    const row = rows[boneIndexAtY(y, m)];
    if (!row || hitAt(x, y, rows, m)) return;

    const frame = Math.max(0, Math.round(xToFrame(x, m)));
    const bone: CanonicalBoneId = row.bone;
    const rotation =
      state.livePose[bone] ?? selectEvaluatedPose(state, frame)[bone] ?? identityQuat();
    // Same rule as the rotation: the key records the posture on screen. `hipsY` is ignored for
    // every bone but Hips, so it can be passed unconditionally.
    store.actions.insertKeyAt(bone, frame, rotation, { hipsY: state.liveHipsY });
  }

  function onContextMenu(event: MouseEvent): void {
    const state = store.getState();
    if (state.mode !== 'animate') return;
    const rows = rowsNow();
    const m = metricsFor(rows.length);
    const { x, y } = pointerPos(event);
    const hit = hitAt(x, y, rows, m);
    if (!hit) return;
    event.preventDefault();

    const refs = refsForHit(rows, hit);
    if (!isWholeSelectionAlready(refs)) store.actions.selectKeys(refs);

    menu?.close();
    menu = openKeyContextMenu({
      x: event.clientX,
      y: event.clientY,
      host: element,
      onDelete: () => store.actions.deleteSelectedKeys(),
      onCopy: () => store.actions.copyKeys(),
      onReverse: () => store.actions.reverseSelectedKeys(),
      onScale: (factor) => store.actions.scaleSelectedKeys(factor),
      onEasing: (preset) => store.actions.setEasingForSelection(preset),
    });
  }

  function onWheel(event: WheelEvent): void {
    const state = store.getState();
    if (state.mode !== 'animate') return;
    const rows = rowsNow();
    const m = metricsFor(rows.length);
    event.preventDefault();

    if (event.ctrlKey || event.metaKey) {
      const factor = Math.exp(-event.deltaY * 0.002);
      store.actions.setZoom(state.timeline.zoom * factor);
      return;
    }
    const pixels = event.deltaX !== 0 ? event.deltaX : event.deltaY;
    store.actions.setScrollFrame(state.timeline.scrollFrame + pixels / pixelsPerFrame(m.zoom));
  }

  function capture(event: PointerEvent): void {
    if (typeof canvas.setPointerCapture === 'function') {
      try {
        canvas.setPointerCapture(event.pointerId);
      } catch {
        /* not available (jsdom) — window listeners already cover the drag */
      }
    }
  }

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('dblclick', onDoubleClick);
  canvas.addEventListener('contextmenu', onContextMenu);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);

  let observer: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(() => redraw());
    observer.observe(element);
  }

  return {
    element,
    canvas,
    redraw,
    dispose(): void {
      disposed = true;
      menu?.close();
      menu = null;
      observer?.disconnect();
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('dblclick', onDoubleClick);
      canvas.removeEventListener('contextmenu', onContextMenu);
      canvas.removeEventListener('wheel', onWheel);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      element.remove();
    },
  };
}
